/**
 * <StatsStrip> — 3 or 4 metrics in a row, used as a credibility anchor
 * (auth page brand panel) or dashboard hero summary.
 *
 *   ORGANIZATIONS    AVG COMPLIANCE    JURISDICTIONS
 *   1,200+           98.4%             60+
 *
 * Usage:
 *   <StatsStrip
 *     stats={[
 *       { label: 'Organizations',     value: '1,200+' },
 *       { label: 'Avg compliance',    value: '98.4%', accent: true },
 *       { label: 'Jurisdictions',     value: '60+' },
 *     ]}
 *   />
 */
import React from 'react';
import styles from './StatsStrip.module.css';

export interface Stat {
  label: string;
  value: React.ReactNode;
  /** If true, the value gets the orange brand accent color. */
  accent?: boolean;
}

interface StatsStripProps {
  stats: Stat[];
  /** Visual surface — 'light' (page) or 'dark' (brand panel / hero card). */
  surface?: 'light' | 'dark';
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export const StatsStrip: React.FC<StatsStripProps> = ({
  stats,
  surface = 'light',
  align = 'left',
  className = '',
}) => {
  return (
    <div
      className={`${styles.strip} ${styles[`surface_${surface}`]} ${styles[`align_${align}`]} ${className}`}
      role="group"
      aria-label="Key statistics"
    >
      {stats.map((s, i) => (
        <div key={i} className={styles.stat}>
          <div className={styles.label}>{s.label.toUpperCase()}</div>
          <div className={`${styles.value} ${s.accent ? styles.valueAccent : ''}`}>
            {s.value}
          </div>
        </div>
      ))}
    </div>
  );
};
